import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MuscleGroupVolume, getMuscleGroupColor } from "@/lib/volume-balance";

interface VolumeDistributionChartProps {
  muscleGroups: MuscleGroupVolume[];
  title?: string;
}

export function VolumeDistributionChart({ 
  muscleGroups, 
  title = "Muscle Group Distribution" 
}: VolumeDistributionChartProps) {
  if (muscleGroups.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>No workout data available</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="chart-height-md flex items-center justify-center text-muted-foreground">
            Complete some workouts to see your volume distribution
          </div>
        </CardContent>
      </Card>
    );
  }

  // Take top 8 muscle groups, combine rest into "Other"
  const topMuscles = muscleGroups.slice(0, 8);
  const otherMuscles = muscleGroups.slice(8);
  
  const chartData = [
    ...topMuscles.map((mg) => ({
      name: mg.muscleGroup.charAt(0).toUpperCase() + mg.muscleGroup.slice(1),
      value: mg.sets,
      percentage: mg.percentage,
      color: getMuscleGroupColor(mg.muscleGroup),
    })),
    ...(otherMuscles.length > 0 ? [{
      name: "Other",
      value: otherMuscles.reduce((sum, mg) => sum + mg.sets, 0),
      percentage: otherMuscles.reduce((sum, mg) => sum + mg.percentage, 0),
      color: "hsl(220, 10%, 60%)",
    }] : []),
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-sm border border-border bg-card px-2 py-1.5 text-[13px] shadow-sm">
          <div className="text-muted-foreground">{data.name}</div>
          <div className="font-mono tabular-nums">
            {data.value} sets ({data.percentage.toFixed(1)}%)
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    if (!payload?.length) return null;
    return (
      <div className="flex items-center justify-center flex-wrap gap-3 mt-3">
        {payload.map((p: any, i: number) => (
          <div key={i} className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
            {p.value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>Sets by muscle group</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="chart-height-md" role="img" aria-label="Muscle group volume distribution pie chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={72}
                outerRadius={90}
                paddingAngle={1}
                dataKey="value"
                animationDuration={350}
                animationEasing="ease-out"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="hsl(var(--card))" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} animationDuration={150} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                content={<CustomLegend />}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
